import { getId } from '@/assets/js/utils';
import { EventBus } from '@/assets/js/event-bus';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import DocumentStatus from '@/assets/js/enum/DocumentStatus';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
import { xhr } from '@/xhr/endpoints';
import { prepAnnotation, prepNodes, matchAnnotationsToNode } from '@/utils/documentHelpers';

//
// state
const state = {
  menuPosition: {
    x: -100,
    y: -100,
  },
  menuFadeOut: false,
  menuFadeOutTimeout: null,
  menuPositionTimeout: null,

  // New state for coordinating with components
  shouldFocusAnnotation: null,
  shouldClearSelection: false,
  scrollerOffset: 0,

  showCounterpointSelect: false,
  selectedItem: null,
  selectedInstruction: {},
  showAnnotationLikes: false,
  threadLikesCounts: {},
  annotationActive: null,
  editingAnnotationId: null,
  emptyAnnotation: false,
  savingAnnotation: [],
  annotationPublicLikesTotalsMap: {},
  annotationPublicLikesFlag: {},

  cachedInlineNodeAnnotations: {},
};

//
// getters
const getters = {
  annotationTypeLabel: () => annotation =>
    annotation?.type === 'instruction' ? 'Inline Rule' : 'Annotation',

  isAnnotationDisabled: (state, getters) => (cpData, annotation) => {
    const isRoundPublished = cpData.status === RoundStatus.PUBLISHED;
    const isMyLatestRound = getters.myLatestRoundNumber === cpData.round;
    const isAuthor = [getId(cpData.author), getId(annotation.author)].includes(getters.userId);

    const isHost = getters.permissionLevel === PermissionLevel.HOST;
    const isHostInstructionsMode = isHost && getters.roundStatus === RoundStatus.HOST;
    const isHostInstructionAnnotation = !isHost && annotation.type === 'hostInstruction';

    const isDrafting = getters.roundStatus === RoundStatus.DRAFTING;
    const isCohortBlocked = false;
    const isMemberBlocked =
      getters.permissionLevel === PermissionLevel.TEAM_MEMBER &&
      (!isDrafting || getters.teamAnnotationsSubmitted);

    const isCohort = getters.permissionLevel === PermissionLevel.COHORT;

    const hasTeamMemberWithCohort = state.currentTeam?.members?.some(
      member =>
        (member.status === 'Accepted' || member.status === 'Invited') &&
        member.teamRole === PermissionLevel.COHORT
    );

    const isCohortTeam = isCohort || hasTeamMemberWithCohort;

    const roundBlocked = !isCohortTeam && isRoundPublished;

    const canEdit =
      (isMyLatestRound && isAuthor) || isHostInstructionsMode || (isCohortTeam && isAuthor);

    return (
      roundBlocked || !canEdit || isHostInstructionAnnotation || isMemberBlocked || isCohortBlocked
    );
  },

  arrowsArray: state => state.counterpointData?.counterpointsFlat || [],

  getSelectedItem: state => state.selectedItem,

  hasSelectedText: () => annotation => !!annotation.selectedText?.from?.nodeKey,
  inlineNodeAnnotations: state => annotation => {
    const cacheKey = annotation._id;

    if (state.cachedInlineNodeAnnotations[cacheKey]) {
      return state.cachedInlineNodeAnnotations[cacheKey];
    }

    const rounds = state.counterpointData.rounds;
    const nodes = annotation.nodes;

    if (!rounds || !nodes) {
      return {};
    }

    const preppedNodes = prepNodes(nodes);

    // Build counterpoints for inline annotations (those referencing this annotation)
    const preppedRounds = rounds.map(cpData => ({
      _id: cpData._id,
      author: cpData.author,
      team: cpData.team,
      annotations: (cpData.annotations || [])
        .filter(ann => ann.refAnnotation?._id === annotation._id)
        .map(prepAnnotation)
        .filter(a => a !== null),
    }));

    // Build spatial index for inline annotations
    const spatialIndex = {
      numeric: new Map(),
      outline: new Map(),
    };

    for (const cp of preppedRounds) {
      for (const ann of cp.annotations) {
        if (ann.numeric) {
          for (let order = ann.fromOrder; order <= ann.toOrder; order++) {
            if (!spatialIndex.numeric.has(order)) {
              spatialIndex.numeric.set(order, []);
            }
            spatialIndex.numeric.get(order).push(ann);
          }
        } else if (ann.outlineId) {
          if (!spatialIndex.outline.has(ann.outlineId)) {
            spatialIndex.outline.set(ann.outlineId, new Map());
          }
          const outlineMap = spatialIndex.outline.get(ann.outlineId);
          for (let order = ann.fromOrder; order <= ann.toOrder; order++) {
            if (!outlineMap.has(order)) {
              outlineMap.set(order, []);
            }
            outlineMap.get(order).push(ann);
          }
        }
      }
    }

    const result = Object.create(null);

    for (const node of preppedNodes) {
      const { annIDs, nodeCPs } = matchAnnotationsToNode(node, preppedRounds, spatialIndex);

      result[node.nodeKey] = {
        counterpoints: nodeCPs,
        counterpointAnnotationIDs: annIDs,
      };
    }

    state.cachedInlineNodeAnnotations[cacheKey] = result;
    return result;
  },
};

//
// commit
const mutations = {
  /**
   *  Team Replies
   **/
  showTeamReplies(state) {
    state.showCounterpointSelect = true;
  },
  hideTeamReplies(state) {
    state.showCounterpointSelect = false;
  },
  setSelectedItem: (state, { type, item }) => {
    state.selectedItem = { type, item };
  },

  setMenuFadeOut: (state, value) => (state.menuFadeOut = value),
  setSelectedInstruction: (state, data) => (state.selectedInstruction = data),
  setAnnotationPublicLikesTotalsMap: (state, value) =>
    (state.annotationPublicLikesTotalsMap = value),
  setAnnotationPublicLikesFlag: (state, value) => (state.annotationPublicLikesFlag = value),
  setShowAnnotationLikes: (state, value) => (state.showAnnotationLikes = value),
  setThreadLikesCounts: (state, value) => (state.threadLikesCounts = value),
  setAnnotationActive: (state, value) => (state.annotationActive = value),
  setEditingAnnotationId: (state, value) => (state.editingAnnotationId = value),
  setEmptyAnnotation: (state, value) => (state.emptyAnnotation = value),
  setSavingAnnotation: (state, value) => (state.savingAnnotation = value),
  removeSavingAnnotation: (state, value) => {
    let index = state.savingAnnotation.findIndex(item => item === value);
    if (index >= 0) {
      state.savingAnnotation.splice(index, 1);
    }
  },

  setShouldFocusAnnotation: (state, value) => (state.shouldFocusAnnotation = value),
  setShouldClearSelection: (state, value) => (state.shouldClearSelection = value),
  setScrollerOffset: (state, value) => (state.scrollerOffset = value),

  setMenuPosition: (state, { x, y }) => {
    state.menuPosition.x = x;
    state.menuPosition.y = y;
  },
  resetMenuPosition: state => {
    state.menuPosition.x = -100;
    state.menuPosition.y = -100;
  },

  clearInlineNodeAnnotationsCache: state => {
    state.cachedInlineNodeAnnotations = {};
  },
};

//
// actions
const actions = {
  /* *********** */
  /* Markup Menu */
  /* *********** */

  positionMenu({ state, getters, commit }, { event, menuEl }) {
    const isHostMode = getters.permissionLevel === PermissionLevel.HOST;
    const debateStarted = getters.debateStarted;
    const userIsHost =
      state.documentData.organization &&
      state.documentData.organization.members.find(
        x => x.role === 'Admin' && x.user && x.user._id === getters.userId
      );
    const documentInHostStatus = state.documentData.status === 'Host';
    const isDraftOrReview = state.debateStatus?.rounds?.length <= 0;
    const isRoundZero = state.counterpointData.rounds.length < 1;
    const myLatestRound = getters.myLatestRound;
    const roundStatus = getters.roundStatus;
    const isCohort = getters.permissionLevel === PermissionLevel.COHORT;

    const isMyRoundActive =
      isCohort ||
      (isHostMode && userIsHost && documentInHostStatus && !debateStarted) ||
      (myLatestRound && getters.roundActive(myLatestRound.round)) ||
      roundStatus === RoundStatus.EDITING_NLP_OUTLINE;

    const isRoundAwaiting =
      !isCohort &&
      getters.permissionLevel !== PermissionLevel.HOST &&
      myLatestRound?.status === RoundStatus.PUBLISHED;

    const teamMemberBlocked = false;
    const outlineBubble = roundStatus === RoundStatus.EDITING_NLP_OUTLINE;

    const canShowMenu =
      ((isDraftOrReview && !isHostMode) || !isRoundZero) &&
      isMyRoundActive &&
      !isRoundAwaiting &&
      !teamMemberBlocked;

    if (canShowMenu) {
      const x = event.clientX - menuEl.offsetWidth / 2;
      const y = !outlineBubble
        ? event.clientY + state.scrollerOffset - menuEl.offsetHeight / 2
        : event.clientY - menuEl.offsetHeight / 2;
      commit('setMenuPosition', { x, y });
    }
  },

  resetMenu({ state, commit }, clearState) {
    commit('setAnnotationActive', null);
    commit('resetMenuPosition');
    if (clearState) {
      commit('clearNewAnnotationPosition');
    }
    commit('setMenuFadeOut', false);

    // Clear any pending timeouts to prevent them from firing after menu is reset
    clearTimeout(state.menuFadeOutTimeout);
    clearTimeout(state.menuPositionTimeout);
    state.menuFadeOutTimeout = null;
    state.menuPositionTimeout = null;
  },

  async addAnnotation({ dispatch }, { annotationType, reponseRoundAnn }) {
    let annotation_id = await dispatch('createNewAnnotation', { annotationType, reponseRoundAnn });
    dispatch('resetMenu', true);
    return annotation_id;
  },

  setSelectedAnnotation: ({ commit }, { counterpoint, annotation }) => {
    commit('setSelectedCounterpoint', counterpoint);
    commit('setSelectedItem', { type: 'annotation', item: annotation });
  },

  setEditingAnnotationId: ({ commit }, annotationId) => {
    commit('setEditingAnnotationId', annotationId);
  },

  fadeOutMenu({ state, commit, dispatch }) {
    commit('setMenuFadeOut', true);
    clearTimeout(state.menuPositionTimeout);
    state.menuPositionTimeout = setTimeout(function () {
      dispatch('resetMenu', true);
    }, 2500);
  },

  showHideBubbleMenu({ state, getters, commit, dispatch }, { event, menuEl }) {
    commit('setMenuFadeOut', false);
    clearTimeout(state.menuFadeOutTimeout);
    clearTimeout(state.menuPositionTimeout);
    state.menuFadeOutTimeout = setTimeout(function () {
      dispatch('fadeOutMenu');
    }, 2500);

    if (
      [DocumentStatus.DRAFT, DocumentStatus.REVIEW].includes(state.counterpointData.status) ||
      !state.counterpointData.rounds.length
    ) {
      return;
    }

    EventBus.emit('menu.checkShouldReset', {
      event,
      callback: shouldReset => {
        if (shouldReset) {
          dispatch('resetMenu', true);
        }
      },
    });

    EventBus.emit('menu.checkIsMenuOption', {
      event,
      callback: isMenuOption => {
        if (
          isMenuOption ||
          !getters.getSelectedItem ||
          (getters.getSelectedItem.type === 'annotation' &&
            state.selectedCounterpoint &&
            getId(state.selectedCounterpoint.team) === getId(getters.teamID))
        ) {
          dispatch('resetMenu', false);
        } else if (
          getters.getSelectedItem.item.roundCounterpoint?.round ===
          getters.myLatestRound?.round - 1
        ) {
          EventBus.emit('menu.checkTargetAndPosition', { event, menuEl });
        }
      },
    });

    setTimeout(() => {
      dispatch('resetMenu', false);
    }, 5000);
  },

  async updateAnnotationText(
    { state, dispatch, commit },
    { annotation, newText, oldText, editedByTeamLead }
  ) {
    const isInstruction = ['instruction', 'hostInstruction'].includes(annotation.type);
    const hasCommentText =
      annotation.comment && annotation.comment.replace(/<[/]?p>/g, '').trim() === '';
    const hasReplies = annotation.selectedReply;
    const textField = isInstruction && hasCommentText && !hasReplies ? 'instruction' : 'comment';

    if (newText !== oldText) {
      commit('setSavingAnnotation', [...state.savingAnnotation, annotation._id]);
      let dataToUpdate = {
        [textField]: newText,
      };

      if (annotation.level === 'Curator') {
        dataToUpdate['markAsUpdated'] = true;
      }
      if (editedByTeamLead) {
        dataToUpdate.editedBy = {
          _id: state.userData.id,
          name: state.userData.display_name,
        };
        if (!annotation.oldComment) {
          dataToUpdate.oldComment = oldText;
        }
      }

      let res = null;
      try {
        res = await xhr.updateAnnotation(annotation._id, dataToUpdate);

        annotation[textField] = res[textField];

        // Clear document cached data when annotation is updated
        commit('clearComputedData');
        // Clear inline node annotations cache
        commit('clearInlineNodeAnnotationsCache');

        // Refetch parent annotation if this annotation has a parent to update canUpdateOrDelete
        if (annotation.refAnnotation?._id) {
          try {
            const parentAnnotation = await xhr.getAnnotation(annotation.refAnnotation._id);
            // Find and update the parent annotation in the store
            const parentRound = state.counterpointData?.rounds?.find(r =>
              r.annotations?.some(ann => ann._id === annotation.refAnnotation._id)
            );
            if (parentRound) {
              const parentIndex = parentRound.annotations.findIndex(
                ann => ann._id === annotation.refAnnotation._id
              );
              if (parentIndex !== -1) {
                // Update canUpdateOrDelete property on parent
                parentRound.annotations[parentIndex].canUpdateOrDelete =
                  parentAnnotation.canUpdateOrDelete;
              }
            }
          } catch (parentErr) {
            console.error(
              '[ANNOTATION_DEBUG] Error refetching parent annotation after update:',
              parentErr
            );
          }
        }

        commit('removeSavingAnnotation', annotation._id);
        //
        // send success result
        EventBus.emit('annotation.save.status', 'Success');
      } catch (err) {
        console.error(err);
        EventBus.emit('annotation.save.status', 'Error');
        dispatch('showToastAlert', {
          title: 'Error updating annotation',
          message: err,
        });
        return false;
      }
      //
      // reset UI

      return res;
    }

    return null;
  },

  async switchAnnotationType(
    { state, getters, dispatch, commit },
    { annotation, dataToUpdate, parentCounterpoint }
  ) {
    let res = null;
    try {
      res = await xhr.switchAnnotation(
        state.counterpointData._id,
        parentCounterpoint ? parentCounterpoint._id : getters.myLatestRound._id,
        annotation._id,
        dataToUpdate
      );
      await dispatch('loadCounterpoints', res);
      commit('clearInlineNodeAnnotationsCache');
    } catch (err) {
      dispatch('showToastAlert', {
        title: 'Error updating annotation',
        message: err,
      });
    }
    // reset UI
    if (state?.selectedItem?.type === 'outlineNode') dispatch('loadOutlineTree');
    //
    await dispatch('reorderAnnotations');

    return res;
  },

  async deleteAnnotation({ state, getters, dispatch, commit }, { annotation, success, failed }) {
    if (getters.permissionLevel === PermissionLevel.HOST) {
      const refByAnnotation = state.counterpointData.refByAnnotationsMap[annotation._id];
      try {
        // Handle case where roundCounterpoint might be undefined
        const annotationRoundId = annotation.roundCounterpoint?._id;
        const refAnnotationRoundId =
          refByAnnotation?.length && refByAnnotation[0].roundCounterpoint?._id
            ? refByAnnotation[0].roundCounterpoint._id
            : null;

        await xhr.deleteHostInlineRule(
          state.counterpointData._id,
          annotationRoundId,
          refAnnotationRoundId,
          {
            _id: annotation._id,
            selectedText: annotation.selectedText,
          },
          refByAnnotation
            ? {
                _id: refByAnnotation[0]._id,
              }
            : {}
        );

        // @TODO Finish splice when testing for Host annotations
        const round = state.counterpointData.rounds;
        let annotationIdx = round.annotations.findIndex(ann => ann._id === annotation._id);
        round.annotations.splice(annotationIdx, 1);
      } catch (err) {
        dispatch('showToastAlert', {
          title: 'Error deleting annotation',
          message: err,
        });
        if (failed) failed(err);
      }
    } else {
      try {
        //
        // find annotation index in current round
        // Handle case where roundCounterpoint might be undefined
        let round;
        if (annotation.roundCounterpoint && annotation.roundCounterpoint.round !== undefined) {
          // For cohort teams: find the annotation in its actual round
          // For curator teams: use existing logic
          const isCohort = getters.permissionLevel === PermissionLevel.COHORT;
          const hasTeamMemberWithCohort = state.currentTeam?.members?.some(
            member =>
              (member.status === 'Accepted' || member.status === 'Invited') &&
              member.teamRole === PermissionLevel.COHORT
          );
          const isCohortTeam = isCohort || hasTeamMemberWithCohort;

          if (isCohortTeam) {
            // Find the annotation in its actual round
            round = state.counterpointData.rounds.find(
              r => r.round === annotation.roundCounterpoint.round
            );
          } else {
            // Curator logic: use myResponseRound or myLatestRound
            round =
              annotation.roundCounterpoint.round % 2 === 0 &&
              state.documentData.discussionType === 'curate'
                ? getters.myResponseRound
                : getters.myLatestRound;
          }
        } else {
          // Fallback: use latest round if roundCounterpoint is not available
          round = getters.myLatestRound;
        }

        const annID = annotation._id;
        const annotationIdx = round.annotations.findIndex(ann => ann._id === annID);
        //
        // remove annotation from array
        if (annotationIdx !== -1) {
          round.annotations.splice(annotationIdx, 1);
        } else {
          console.warn('[DEBUG] deleteAnnotation - annotation not found in round!');
        }

        // Clear document cached data when annotation is deleted
        commit('clearComputedData');
        commit('clearInlineNodeAnnotationsCache');

        if (success) success();

        await xhr.deleteAnnotation(annID);
      } catch (err) {
        dispatch('showToastAlert', {
          title: 'Error deleting annotation',
          message: err,
        });
        if (failed) failed(err);
      }
    }

    //
    // reset UI
    dispatch('setupCounterpoints');
    commit('clearInlineNodeAnnotationsCache');
    //
    if (state.selectedItem.type === 'outlineNode') dispatch('loadOutlineTree');
    //
    await dispatch('reorderAnnotations');
    //
    // call success function if exists
    if (success) success();
    return true;
  },
};

//
// exports
export default {
  // namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
